import { useState, useEffect } from 'react';
import { X, Play, Star, Award, Zap } from 'lucide-react';
import type { Game } from '../hooks/useGames';

interface GameModalProps {
    game: Game | null;
    isOpen: boolean;
    onClose: () => void;
    onPlay: (game: Game) => void | Promise<void>;
}

const GameModal = ({ game, isOpen, onClose, onPlay }: GameModalProps) => {
    const [imageLoaded, setImageLoaded] = useState(false);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setImageLoaded(false);
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen || !game) return null;

    const formatJackpot = (value: number) => {
        if (value >= 1000000) {
            return `€${(value / 1000000).toFixed(1)}M`;
        } else if (value >= 1000) {
            return `€${(value / 1000).toFixed(0)}K`;
        }
        return `€${value}`;
    };

    const getVolatilityLabel = (volatility: string) => {
        switch (volatility) {
            case '1':
            case '2': return 'Bassa';
            case '3': return 'Media';
            case '4':
            case '5': return 'Alta';
            default: return 'N/A';
        }
    };

    const handlePlay = () => {
        onPlay(game);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Overlay */}
            <div
                className="absolute inset-0 bg-black bg-opacity-80 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative bg-gray-900 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header con immagine */}
                <div className="relative">
                    <img
                        src={game.images?.landscapeImgURL || game.images?.thumbURL}
                        alt={game.name}
                        className={`w-full h-64 object-cover rounded-t-lg transition-opacity duration-300 ${
                            imageLoaded ? 'opacity-100' : 'opacity-0'
                        }`}
                        onLoad={() => setImageLoaded(true)}
                    />

                    {!imageLoaded && (
                        <div className="w-full h-64 bg-gray-800 rounded-t-lg animate-pulse" />
                    )}

                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent rounded-t-lg" />

                    {/* Close button */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-colors"
                    >
                        <X size={20} />
                    </button>

                    {/* Jackpot badge */}
                    {game.info?.jackpot && game.info?.jackpotValue && (
                        <div className="absolute top-4 left-4 bg-yellow-500 text-black px-3 py-1 rounded-full font-bold flex items-center space-x-1">
                            <Award size={16} />
                            <span>{formatJackpot(game.info.jackpotValue)}</span>
                        </div>
                    )}
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Title and provider */}
                    <div className="mb-6">
                        <h2 className="text-2xl font-bold text-white mb-2">{game.name}</h2>
                        <p className="text-gray-400">Sviluppato da {game.provider}</p>
                    </div>

                    {/* Game stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        {game.info?.rtp && (
                            <div className="bg-gray-800 rounded-lg p-3 text-center">
                                <div className="text-green-500 font-bold text-lg">{game.info.rtp}%</div>
                                <div className="text-gray-400 text-sm">RTP</div>
                            </div>
                        )}

                        {game.info?.lines && (
                            <div className="bg-gray-800 rounded-lg p-3 text-center">
                                <div className="text-blue-500 font-bold text-lg">{game.info.lines}</div>
                                <div className="text-gray-400 text-sm">Linee</div>
                            </div>
                        )}

                        {game.info?.volatility && (
                            <div className="bg-gray-800 rounded-lg p-3 text-center">
                                <div className="text-purple-500 font-bold text-lg flex items-center justify-center">
                                    <Zap size={16} className="mr-1" />
                                    {getVolatilityLabel(game.info.volatility)}
                                </div>
                                <div className="text-gray-400 text-sm">Volatilità</div>
                            </div>
                        )}

                        {game.info?.jackpot && (
                            <div className="bg-gray-800 rounded-lg p-3 text-center">
                                <div className="text-yellow-500 font-bold text-lg flex items-center justify-center">
                                    <Star size={16} className="mr-1" />
                                    Sì
                                </div>
                                <div className="text-gray-400 text-sm">Jackpot</div>
                            </div>
                        )}
                    </div>

                    {/* Features */}
                    {game.info?.functionality && game.info.functionality.length > 0 && (
                        <div className="mb-6">
                            <h3 className="text-white font-semibold mb-3">Caratteristiche</h3>
                            <div className="flex flex-wrap gap-2">
                                {game.info.functionality.map((feature, index) => (
                                    <span
                                        key={index}
                                        className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm"
                                    >
                    {feature}
                  </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Themes */}
                    {game.info?.theme && game.info.theme.length > 0 && (
                        <div className="mb-6">
                            <h3 className="text-white font-semibold mb-3">Temi</h3>
                            <div className="flex flex-wrap gap-2">
                                {game.info.theme.map((theme, index) => (
                                    <span
                                        key={index}
                                        className="bg-gray-700 text-gray-300 px-3 py-1 rounded-full text-sm"
                                    >
                    {theme}
                  </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Play button */}
                    <button
                        onClick={handlePlay}
                        className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                    >
                        <Play size={20} fill="currentColor" />
                        <span>Gioca Ora</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GameModal;